import { useEffect, useRef, useState } from "react";
import { Volume2, Loader2 } from "lucide-react";

type YTPlayer = {
  playVideo: () => void;
  destroy: () => void;
};

type YTNamespace = {
  Player: new (
    element: HTMLElement | string,
    config: {
      videoId: string;
      playerVars?: Record<string, number>;
      events?: {
        onReady?: (event: { target: YTPlayer }) => void;
        onStateChange?: (event: { data: number; target: YTPlayer }) => void;
      };
    },
  ) => YTPlayer;
};

declare global {
  interface Window {
    YT?: YTNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

type Props = {
  videoId: string;
  teaserSrc: string;
};

const VSLPlayer = ({ videoId, teaserSrc }: Props) => {
  const loopVideoRef = useRef<HTMLVideoElement>(null);
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YTPlayer | null>(null);

  const [soundUnlocked, setSoundUnlocked] = useState(false);
  const [isStartingVideo, setIsStartingVideo] = useState(false);

  useEffect(() => {
    if (!soundUnlocked && loopVideoRef.current) {
      loopVideoRef.current.play().catch(() => {
        // Autoplay can be blocked in some contexts — ignore silently.
      });
    }
  }, [soundUnlocked]);

  useEffect(() => {
    const createPlayer = () => {
      if (!playerContainerRef.current || !window.YT?.Player) return;
      playerRef.current = new window.YT.Player(playerContainerRef.current, {
        videoId,
        playerVars: {
          autoplay: 0,
          controls: 1,
          fs: 1,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          iv_load_policy: 3,
          cc_load_policy: 0,
        },
        events: {
          onStateChange: (event) => {
            // YT.PlayerState.PLAYING === 1 — only hide the teaser once the
            // video has actually started, to avoid a thumbnail flash on tap.
            if (event.data === 1) {
              setSoundUnlocked(true);
              setIsStartingVideo(false);
            }
          },
        },
      });
    };

    let ourCallback: (() => void) | undefined;
    let priorCallback: (() => void) | undefined;

    if (window.YT?.Player) {
      createPlayer();
    } else {
      const scriptSrc = "https://www.youtube.com/iframe_api";
      if (!document.querySelector(`script[src="${scriptSrc}"]`)) {
        const tag = document.createElement("script");
        tag.src = scriptSrc;
        document.body.appendChild(tag);
      }
      priorCallback = window.onYouTubeIframeAPIReady;
      ourCallback = () => {
        priorCallback?.();
        createPlayer();
      };
      window.onYouTubeIframeAPIReady = ourCallback;
    }

    return () => {
      // Only restore if we're still the top of the chain; otherwise a later
      // mount has chained after us and will handle its own cleanup.
      if (ourCallback && window.onYouTubeIframeAPIReady === ourCallback) {
        window.onYouTubeIframeAPIReady = priorCallback;
      }
      playerRef.current?.destroy();
      playerRef.current = null;
    };
  }, [videoId]);

  const unlockSound = () => {
    if (isStartingVideo || soundUnlocked) return;
    playerRef.current?.playVideo();
    setIsStartingVideo(true);
  };

  return (
    <div className="relative w-full aspect-video rounded-xl overflow-hidden shadow-xl bg-navy">
      <div
        className={`absolute inset-0 w-full h-full ${
          soundUnlocked ? "visible" : "invisible"
        }`}>
        <div ref={playerContainerRef} className="w-full h-full" />
      </div>

      {!soundUnlocked && (
        <button
          type="button"
          onClick={unlockSound}
          className="absolute inset-0 w-full h-full group z-10"
          aria-label="Click to play video with sound">
          <video
            ref={loopVideoRef}
            src={teaserSrc}
            muted
            loop
            autoPlay
            playsInline
            preload="auto"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-navy/30 group-hover:bg-navy/40 transition-colors" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              <span className="absolute inset-0 rounded-full bg-gold/50 animate-pulse-ring" />
              <span
                className="absolute inset-0 rounded-full bg-gold/40 animate-pulse-ring"
                style={{ animationDelay: "0.6s" }}
              />
              <div className="relative inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-gold text-navy text-sm font-semibold shadow-xl animate-pulse-cta">
                {isStartingVideo ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading…
                  </>
                ) : (
                  <>
                    <Volume2 className="w-4 h-4" />
                    Click for sound
                  </>
                )}
              </div>
            </div>
          </div>
        </button>
      )}
    </div>
  );
};

export default VSLPlayer;
